import type {BaseAPI, PublicApi} from '@linx-security/api-specs';
import type {Key} from 'swr';
import {preload as SWRPreload} from 'swr';

import type {AvailableEndpoints} from './types';

/**
 * @description A typescript wrapper to SWR preload function with our OpenAPI clients.
 *  You should use this function to preload data from the API out side of the component (see SWR docs for more info).
 *  https://swr.vercel.app/docs/prefetching
 * @example
 *  preload(client, `getUsers`, {limit: 10, offset: 0});
 *
 *  const UsersPage = () => { ... }
 */
export const preload = <
  TClient extends BaseAPI | PublicApi,
  TEndpoint extends AvailableEndpoints<TClient>,
  TEndpointFunction extends TClient[TEndpoint] extends (...args: any) => any ? TClient[TEndpoint] : never,
  TParams extends Parameters<TEndpointFunction>[0],
  TResponse extends Awaited<ReturnType<TEndpointFunction>>,
>(
  client: TClient,
  endpoint: TEndpoint,
  request: TParams = {} as TParams,
) => {
  const fetcher = ([endpoint, params]: [TEndpoint, TParams]) => {
    if (!(endpoint in client) || typeof client[endpoint] !== `function`) return null;
    return (client[endpoint] as Function).apply(client, [params]);
  };

  return SWRPreload<TResponse, Key, typeof fetcher>([endpoint, endpoint, request], () => fetcher([endpoint, request]));
};
